import { toObservable } from "@angular/core/rxjs-interop";
import { signal } from "@angular/core";
import { addYears } from "date-fns";
import { PeriodType } from "@bitwarden/web-vault/app/models/types/filter.types";
import { BalanceDatesUtils } from "@bitwarden/web-vault/app/services/store/calculation-base/balances/balanceDates.utils";
import { PeriodSelectionController } from "@bitwarden/web-vault/app/services/store/calculation-base/controls/period.control";

export class ScenarioPeriodController extends PeriodSelectionController {
  /** Snapshot of "today" for the two field initializers below, so they agree even if
   * construction straddles a day boundary. */
  private readonly constructionDay = BalanceDatesUtils.getStartOfDayDate(new Date());

  protected _selected = signal<PeriodType>(this.getDefaultSelection(this.constructionDay));
  selected = this._selected.asReadonly();

  private _options = signal<PeriodType>(this.getDefaultSelection(this.constructionDay));
  options = this._options.asReadonly();

  /** **<span style="color: red;">This is for store use only</span>** **/
  readonly selected$ = toObservable(this.selected);

  clear() {
    const today = BalanceDatesUtils.getStartOfDayDate(new Date());
    this._options.set(this.getDefaultSelection(today));
    this._selected.set(this.getDefaultSelection(today));
  }

  /**
   * Update range selection, set _isDefault to false automatically to flag human action
   */
  updateSelection(period: PeriodType) {
    const adjustedPeriod = {
      startDate: BalanceDatesUtils.getStartOfDayDate(period.startDate),
      endDate: BalanceDatesUtils.getEndOfDayDate(period.endDate),
      _isDefault: false,
    };
    this._selected.set(adjustedPeriod);
  }

  /**
   * The default SELECTED range: a today-to-+1-year window. Recomputes "today" from the current
   * wall clock by default so a reset/clear in a long-lived session lands on today's window
   * instead of whenever the controller was constructed; callers that need two signals (e.g.
   * `_selected`/`_options`) to agree on the same "today" can pass it in explicitly. Normalized to
   * day granularity (like the other period controllers) so repeated calls within the same day are
   * referentially stable for callers that cache by value.
   */
  getDefaultSelection(today: Date = BalanceDatesUtils.getStartOfDayDate(new Date())): PeriodType {
    return {
      startDate: today,
      endDate: BalanceDatesUtils.getEndOfDayDate(addYears(today, 1)),
      _isDefault: true,
    };
  }
}
